import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react'
import { strokePaths } from '../utils/strokePaths'
import { evaluateTracePath, calculateDeviationFromPath } from '../utils/pathTracking'
import { remoteLog } from '../../../shared/remoteLog'
import './HandwritingCanvas.css'

// Base sizes for drawing furniture (guideline stroke width, checkpoint dot
// radii, user's path, arrow). Values are in the 200-unit source coordinate
// space and stay constant regardless of the character scale, so the dots
// and guideline keep the same absolute pixel size on a small glyph scaled
// to 90% fill as on a big glyph that renders near 1:1. Only the character
// itself scales — the "furniture" stays a consistent visual weight.
const GUIDE_STROKE_WIDTH = 12
const USER_PATH_WIDTH = 9
const DOT_RADIUS_START = 10.5
const DOT_RADIUS_MID = 7.5
const CLEARED_DOT_RADIUS_START = 8
const CLEARED_DOT_RADIUS_MID = 5
const ARROW_LINE_WIDTH = 2.5
const ARROW_SHAFT_LENGTH = 7
const ARROW_HEAD_LENGTH = 4

// Extra vertical padding (top and bottom) to give kids more room when tracing
const VERTICAL_PADDING = 15

// Difficulty levels: 0 (all guide dots + full guideline) .. 9 (only start/end dots).
const DIFFICULTY_LEVELS = 10

// Return the set of checkpoint indices that should be HIDDEN for a given
// (stroke, difficulty). The rule:
//
//   - `idx 0` (the stroke's start anchor) is always visible so the child
//     knows where to begin the stroke.
//   - The other n-1 checkpoints are ranked by a deterministic per-index
//     score; the top K by score get hidden, where
//     `K = round(n * difficulty / 10)` (capped at n-1). So at difficulty 6,
//     roughly 60% of the stroke's checkpoints are hidden regardless of the
//     stroke length; at difficulty 10, only idx 0 remains.
//   - Same (stroke, strokeIdx, difficulty) → same set (deterministic).
//   - Higher difficulty is a strict superset of lower difficulty
//     (monotonic — the ranking doesn't depend on `difficulty`, so raising
//     it only adds indices to the "hidden" prefix).
export function computeHiddenSet(stroke, strokeIdx, difficulty) {
  const hidden = new Set()
  if (!stroke || difficulty <= 0) return hidden
  const n = stroke.length
  if (n <= 1) return hidden

  const hideableCount = n - 1 // everything except idx 0
  const targetHiddenCount = Math.min(
    Math.round((n * difficulty) / DIFFICULTY_LEVELS),
    hideableCount
  )
  if (targetHiddenCount === 0) return hidden

  // Score each hideable index. The primes give a reasonably spread pattern
  // (avoiding the previous "always hide low indices first" bias).
  const candidates = []
  for (let i = 1; i < n; i++) {
    candidates.push({ idx: i, score: (i * 37 + strokeIdx * 71) % 100 })
  }
  candidates.sort((a, b) => (a.score - b.score) || (a.idx - b.idx))
  for (let k = 0; k < targetHiddenCount; k++) {
    hidden.add(candidates[k].idx)
  }
  return hidden
}

// Whether a checkpoint on the current stroke is visible to the child. Hidden
// checkpoints (from difficulty) only become visible after the trace has
// cleared them — approaching them must not reveal them, or the difficulty
// setting is defeated by the hint.
export function isCheckpointVisibleForCurrentStroke(idx, hiddenSet, clearedSet) {
  if (!hiddenSet.has(idx)) return true
  return clearedSet.has(idx)
}

// Fit the character bounds to the drawing area. Returns the center of the
// character in the source coordinate space and the scale to apply. The
// character is scaled up (or down) so its larger dimension fills exactly
// `targetFillRatio` of the drawing area — with no upper clamp, so small
// glyphs (like the handwriting "0") no longer render at 50% of the canvas.
// `canvasUnits` is the drawing area's size in source coordinate units
// (200 = the base coordinate space used by strokePaths).
export function computeCharacterFit(strokes, canvasUnits = 200, targetFillRatio = 0.9) {
  let minX = Infinity, minY = Infinity
  let maxX = -Infinity, maxY = -Infinity
  ;(strokes || []).forEach((stroke) => {
    stroke.forEach((point) => {
      if (point.x < minX) minX = point.x
      if (point.y < minY) minY = point.y
      if (point.x > maxX) maxX = point.x
      if (point.y > maxY) maxY = point.y
    })
  })
  if (!isFinite(minX)) {
    return { charScale: 1, charCenterX: 0, charCenterY: 0, hasBounds: false }
  }
  const charWidth = maxX - minX
  const charHeight = maxY - minY
  const availableSpace = canvasUnits * targetFillRatio
  const scaleX = charWidth > 0 ? availableSpace / charWidth : Infinity
  const scaleY = charHeight > 0 ? availableSpace / charHeight : Infinity
  let charScale = Math.min(scaleX, scaleY)
  if (!isFinite(charScale)) charScale = 1
  return {
    charScale,
    charCenterX: minX + charWidth / 2,
    charCenterY: minY + charHeight / 2,
    hasBounds: true
  }
}

// Compute the arrow target for the "next uncleared" indicator. The arrow
// points to the IMMEDIATELY-NEXT logical checkpoint in the stroke path,
// even if that checkpoint is hidden by difficulty — otherwise the arrow
// would skip over hidden points and mis-indicate the direction of travel
// on curved strokes.
export function computeArrowTarget(strokePath, nextUnclearedIdx) {
  const arrowIdx = nextUnclearedIdx + 1
  if (arrowIdx >= strokePath.length) {
    return { hasNext: false, nextPoint: null }
  }
  return { hasNext: true, nextPoint: strokePath[arrowIdx] }
}

const HandwritingCanvas = forwardRef(function HandwritingCanvas({ character, customStrokes, difficulty = 0, onSuccess, onCheckpointUpdate, deviationMultiplier = 2.0 }, ref) {
  // Calculate actual pixel threshold from multiplier (2.0 * 12 = 24 pixels by default)
  const deviationThreshold = deviationMultiplier * GUIDE_STROKE_WIDTH
  const canvasRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [userPath, setUserPath] = useState([])
  const [currentStroke, setCurrentStroke] = useState(0)
  const [clearedCheckpoints, setClearedCheckpoints] = useState(new Set())
  const [isStrokeValid, setIsStrokeValid] = useState(true)

  // Diagnostic state - tracks recent events for debugging
  const diagnosticDataRef = useRef({
    touchEvents: [],
    mouseEvents: [],
    meaningfulEvents: [], // Unified log of last 100 meaningful events
    lastCanvasRect: null,
    lastCoordTransform: null,
    touchStartCount: 0,
    lastTouchStartTime: null,
  })

  // Helper to log meaningful events with context
  const logMeaningfulEvent = (eventType, data = {}) => {
    const canvas = canvasRef.current
    const rect = canvas?.getBoundingClientRect()
    const event = {
      type: eventType,
      timestamp: Date.now(),
      currentStroke,
      clearedCheckpoints: Array.from(clearedCheckpoints),
      isDrawing,
      isStrokeValid,
      userPathLength: userPath.length,
      ...data,
    }
    // Include canvas rect for position-related events
    if (rect && ['touchstart', 'touchmove', 'touchend', 'mousedown', 'mousemove', 'mouseup'].includes(eventType)) {
      event.canvasRect = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      }
    }
    diagnosticDataRef.current.meaningfulEvents.push(event)
    // Keep only last 100 events
    if (diagnosticDataRef.current.meaningfulEvents.length > 100) {
      diagnosticDataRef.current.meaningfulEvents.shift()
    }
  }

  // SAFARI TOUCH EVENT FIX: Safari requires a native addEventListener('touchstart') on the
  // canvas element for touch events to be properly dispatched to React's synthetic event
  // handlers (onTouchStart, etc.). Without this, Safari may stop firing touch events after
  // certain re-renders - specifically affecting later strokes in multi-stroke characters.
  // This is a no-op listener; its mere existence fixes Safari's touch event delegation.
  // See: https://github.com/facebook/react/issues/9809 (related React touch event issues)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const safariTouchFix = () => {}
    canvas.addEventListener('touchstart', safariTouchFix, { passive: true })

    return () => {
      canvas.removeEventListener('touchstart', safariTouchFix)
    }
  }, [])

  // Use custom strokes if provided, otherwise use builtin strokePaths
  const strokes = customStrokes || strokePaths[character] || []

  // Expose diagnose function to parent via ref
  useImperativeHandle(ref, () => ({
    diagnose: () => {
      const canvas = canvasRef.current
      const rect = canvas?.getBoundingClientRect()
      const computedStyle = canvas ? window.getComputedStyle(canvas) : null

      // Get the last 100 meaningful events
      const meaningfulEvents = diagnosticDataRef.current.meaningfulEvents.slice(-100)

      const diagnosticInfo = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        character,
        currentStroke,
        totalStrokes: strokes.length,
        clearedCheckpoints: Array.from(clearedCheckpoints),
        totalCheckpoints: strokes[currentStroke]?.length || 0,
        userPathLength: userPath.length,
        userPathSample: userPath.slice(-10), // Last 10 points
        canvas: {
          width: canvas?.width,
          height: canvas?.height,
          clientWidth: canvas?.clientWidth,
          clientHeight: canvas?.clientHeight,
          offsetWidth: canvas?.offsetWidth,
          offsetHeight: canvas?.offsetHeight,
        },
        boundingRect: rect ? {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
          x: rect.x,
          y: rect.y,
        } : null,
        computedStyle: computedStyle ? {
          width: computedStyle.width,
          height: computedStyle.height,
          touchAction: computedStyle.touchAction,
        } : null,
        strokePath: strokes[currentStroke] || [],
        // Last 100 meaningful events with rich context
        meaningfulEvents,
        meaningfulEventsCount: meaningfulEvents.length,
        // Legacy fields for backwards compatibility
        recentTouchEvents: diagnosticDataRef.current.touchEvents.slice(-20),
        recentMouseEvents: diagnosticDataRef.current.mouseEvents.slice(-20),
        lastCoordTransform: diagnosticDataRef.current.lastCoordTransform,
        touchStartCount: diagnosticDataRef.current.touchStartCount,
        lastTouchStartTime: diagnosticDataRef.current.lastTouchStartTime,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio,
          orientation: screen.orientation?.type || 'unknown',
        },
      }

      remoteLog.info('HANDWRITING_DIAGNOSE', diagnosticInfo)
      return diagnosticInfo
    }
  }))

  // Reset when character changes
  useEffect(() => {
    setUserPath([])
    setCurrentStroke(0)
    setClearedCheckpoints(new Set())
    setIsStrokeValid(true)
    // Clear diagnostic events for new character
    diagnosticDataRef.current.touchEvents = []
    diagnosticDataRef.current.meaningfulEvents = []
    diagnosticDataRef.current.touchStartCount = 0
    // Log character change as first meaningful event
    diagnosticDataRef.current.meaningfulEvents.push({
      type: 'character_changed',
      timestamp: Date.now(),
      character,
      totalStrokes: strokePaths[character]?.length || 0,
    })
    drawCanvas()
  }, [character])

  // Clear checkpoints when stroke changes (ensures clean state for new stroke)
  useEffect(() => {
    // Don't clear on initial mount or character change (handled above)
    if (currentStroke > 0) {
      setClearedCheckpoints(new Set())
      setUserPath([])
      setIsStrokeValid(true)
    }
  }, [currentStroke])


  // Redraw canvas when relevant state changes.
  useEffect(() => {
    drawCanvas()
  }, [userPath, currentStroke, character, clearedCheckpoints, isStrokeValid, difficulty])

  // Report checkpoint progress to parent
  useEffect(() => {
    if (onCheckpointUpdate && strokes.length > 0 && currentStroke < strokes.length) {
      const totalCheckpoints = strokes[currentStroke].length
      const clearedCount = clearedCheckpoints.size
      onCheckpointUpdate(clearedCount, totalCheckpoints)
    }
  }, [clearedCheckpoints, currentStroke, strokes, onCheckpointUpdate])

  const drawCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Fit the character into the drawing area at 90% fill. Scales up small
    // glyphs so they don't render at ~50% of the canvas.
    const { charScale, charCenterX, charCenterY } = computeCharacterFit(strokes)

    // Scale to fit 200x200 paths in 256x256 canvas (1.28x scale)
    const canvasScale = 256 / 200
    ctx.save()
    ctx.scale(canvasScale, canvasScale)

    // Only the character's POSITIONS are scaled by charScale — line widths
    // and dot radii stay at their base sizes so a small glyph scaled to 90%
    // fill has the same absolute-pixel dots and guideline as a big glyph
    // that renders near 1:1. Doing this in code (rather than via
    // `ctx.scale(charScale)`) avoids double-scaling stroke widths and
    // arc radii, which the transform would also scale per canvas spec.
    const scaledPoint = (p) => ({
      x: (p.x - charCenterX) * charScale + 100,
      y: (p.y - charCenterY) * charScale + 100 + VERTICAL_PADDING / canvasScale
    })

    // Draw light gray reference strokes (not yet completed). The same
    // difficulty hide pattern is applied to every future stroke of a
    // multi-stroke character (line segments AND preview dots), so a hard
    // difficulty consistently thins guidance across all segments — not just
    // whichever stroke happens to be current.
    ctx.lineWidth = GUIDE_STROKE_WIDTH
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    strokes.forEach((stroke, idx) => {
      if (idx > currentStroke) {
        const hidden = computeHiddenSet(stroke, idx, difficulty)
        ctx.strokeStyle = '#e0e0e0'
        ctx.globalAlpha = 0.3
        drawStrokePathWithHidden(ctx, stroke.map(scaledPoint), hidden, new Set())

        // Preview future-stroke dots so the same "hide by difficulty" rule
        // applies to every stroke of a multi-stroke character, not just the
        // one currently being traced. Hidden checkpoints are omitted.
        stroke.forEach((point, pointIdx) => {
          if (hidden.has(pointIdx)) return
          const radius = pointIdx === 0 ? DOT_RADIUS_START : DOT_RADIUS_MID
          const sp = scaledPoint(point)
          ctx.beginPath()
          ctx.arc(sp.x, sp.y, radius, 0, Math.PI * 2)
          ctx.fillStyle = '#9e9e9e'
          ctx.fill()
        })
        ctx.globalAlpha = 1
      }
    })

    // Draw completed strokes in green
    strokes.forEach((stroke, idx) => {
      if (idx < currentStroke) {
        ctx.strokeStyle = '#4caf50'
        ctx.lineWidth = GUIDE_STROKE_WIDTH
        drawStrokePath(ctx, stroke.map(scaledPoint))

        // Draw completed checkpoints in green
        stroke.forEach((point, pointIdx) => {
          const radius = pointIdx === 0 ? CLEARED_DOT_RADIUS_START : CLEARED_DOT_RADIUS_MID
          const sp = scaledPoint(point)
          ctx.beginPath()
          ctx.arc(sp.x, sp.y, radius, 0, Math.PI * 2)
          ctx.fillStyle = '#4caf50'
          ctx.fill()
        })
      }
    })

    // Variable to store next uncleared checkpoint info for drawing on top later
    let nextUnclearedInfo = null

    // Draw current stroke to trace (highlighted)
    if (currentStroke < strokes.length) {
      const currentStrokePath = strokes[currentStroke]
      const currentHidden = computeHiddenSet(currentStrokePath, currentStroke, difficulty)

      // A hidden checkpoint (and its incident guideline segments) only
      // becomes visible after the child's trace has cleared it — approaching
      // it must not reveal it, or the hint defeats the difficulty.
      const isCheckpointVisible = (idx) =>
        isCheckpointVisibleForCurrentStroke(idx, currentHidden, clearedCheckpoints)

      // Draw guideline segments only between visible checkpoints.
      ctx.strokeStyle = '#2196f3'
      ctx.lineWidth = GUIDE_STROKE_WIDTH
      ctx.globalAlpha = 0.5
      drawStrokePathWithHidden(ctx, currentStrokePath.map(scaledPoint), currentHidden, clearedCheckpoints)
      ctx.globalAlpha = 1

      // Find the next uncleared checkpoint that is also visible; this is the
      // one we highlight in blue. If the next uncleared is hidden by
      // difficulty, no blue dot is drawn — the child must trace to it blind.
      let nextUnclearedIdx = -1
      for (let i = 0; i < currentStrokePath.length; i++) {
        if (!clearedCheckpoints.has(i)) {
          nextUnclearedIdx = i
          break
        }
      }
      const nextUnclearedVisible = nextUnclearedIdx !== -1 && isCheckpointVisible(nextUnclearedIdx)

      // Draw all visible dots EXCEPT the highlighted next-uncleared one
      // (drawn on top later so the user path doesn't obscure it).
      currentStrokePath.forEach((point, idx) => {
        if (!isCheckpointVisible(idx)) return
        if (nextUnclearedVisible && idx === nextUnclearedIdx) return

        const isCleared = clearedCheckpoints.has(idx)
        const radius = idx === 0 ? DOT_RADIUS_START : DOT_RADIUS_MID
        const sp = scaledPoint(point)

        ctx.beginPath()
        ctx.arc(sp.x, sp.y, radius, 0, Math.PI * 2)
        ctx.fillStyle = isCleared ? '#4caf50' : '#9e9e9e'
        ctx.fill()
      })

      if (nextUnclearedVisible) {
        const { hasNext, nextPoint } = computeArrowTarget(currentStrokePath, nextUnclearedIdx)
        nextUnclearedInfo = {
          idx: nextUnclearedIdx,
          point: currentStrokePath[nextUnclearedIdx],
          hasNext,
          nextPoint
        }
      }
    }

    // Draw user's path (green if valid, red if invalid)
    if (userPath.length > 0) {
      ctx.strokeStyle = isStrokeValid ? '#4caf50' : '#f44336'
      ctx.lineWidth = USER_PATH_WIDTH
      ctx.beginPath()
      const first = scaledPoint(userPath[0])
      ctx.moveTo(first.x, first.y)
      userPath.forEach(point => {
        const sp = scaledPoint(point)
        ctx.lineTo(sp.x, sp.y)
      })
      ctx.stroke()
    }

    // Draw the next uncleared checkpoint LAST (on top of user path) in blue
    if (nextUnclearedInfo) {
      const radius = nextUnclearedInfo.idx === 0 ? DOT_RADIUS_START : DOT_RADIUS_MID
      const sp = scaledPoint(nextUnclearedInfo.point)

      ctx.beginPath()
      ctx.arc(sp.x, sp.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = '#2196f3'
      ctx.fill()

      // Draw arrow on the next uncleared dot
      if (nextUnclearedInfo.hasNext) {
        const spNext = scaledPoint(nextUnclearedInfo.nextPoint)
        const angle = Math.atan2(
          spNext.y - sp.y,
          spNext.x - sp.x
        )

        const arrowLength = ARROW_SHAFT_LENGTH
        const arrowHeadLength = ARROW_HEAD_LENGTH

        ctx.strokeStyle = '#ff0000'  // Bright red color
        ctx.lineWidth = ARROW_LINE_WIDTH
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        // Draw all three lines as one path for better visibility
        ctx.beginPath()

        // Center line (points toward next dot)
        ctx.moveTo(sp.x, sp.y)
        ctx.lineTo(
          sp.x + Math.cos(angle) * arrowLength,
          sp.y + Math.sin(angle) * arrowLength
        )

        // Left diagonal line (arrow head)
        const tipX = sp.x + Math.cos(angle) * arrowLength
        const tipY = sp.y + Math.sin(angle) * arrowLength
        ctx.moveTo(tipX, tipY)
        ctx.lineTo(
          tipX + Math.cos(angle + Math.PI * 0.75) * arrowHeadLength,
          tipY + Math.sin(angle + Math.PI * 0.75) * arrowHeadLength
        )

        // Right diagonal line (arrow head)
        ctx.moveTo(tipX, tipY)
        ctx.lineTo(
          tipX + Math.cos(angle - Math.PI * 0.75) * arrowHeadLength,
          tipY + Math.sin(angle - Math.PI * 0.75) * arrowHeadLength
        )

        ctx.stroke()
      }
    }

    ctx.restore()
  }

  const drawStrokePath = (ctx, strokePath) => {
    if (strokePath.length === 0) return
    ctx.beginPath()
    ctx.moveTo(strokePath[0].x, strokePath[0].y)
    strokePath.forEach(point => {
      ctx.lineTo(point.x, point.y)
    })
    ctx.stroke()
  }

  // Like drawStrokePath but skips segments whose endpoints are hidden and not
  // revealed. A segment is drawn only when both endpoints are visible (either
  // not in `hidden`, or in `revealedOrCleared`).
  const drawStrokePathWithHidden = (ctx, strokePath, hidden, revealedOrCleared) => {
    if (strokePath.length === 0) return
    const isVisible = (idx) => !hidden.has(idx) || revealedOrCleared.has(idx)
    ctx.beginPath()
    let penDown = false
    for (let i = 0; i < strokePath.length; i++) {
      const visible = isVisible(i)
      if (!visible) {
        penDown = false
        continue
      }
      const p = strokePath[i]
      if (!penDown) {
        ctx.moveTo(p.x, p.y)
        penDown = true
      } else {
        ctx.lineTo(p.x, p.y)
      }
    }
    ctx.stroke()
  }

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()

    // Must match the transformation used in drawCanvas — same scale, same
    // center — so screen coordinates can be inverted back to source units.
    const { charScale, charCenterX, charCenterY } = computeCharacterFit(strokes)

    const canvasScale = 256 / 200
    const offsetX = 100 - charCenterX * charScale
    const offsetY = 100 - charCenterY * charScale + VERTICAL_PADDING / canvasScale

    // Convert screen coordinates to canvas coordinates
    const canvasHeight = 256 + VERTICAL_PADDING * 2
    const canvasX = ((e.clientX - rect.left) / rect.width) * 256
    const canvasY = ((e.clientY - rect.top) / rect.height) * canvasHeight

    // Reverse the transformations: undo canvas scale, undo character scale, undo translation
    const result = {
      x: (canvasX / canvasScale - offsetX) / charScale,
      y: (canvasY / canvasScale - offsetY) / charScale
    }

    // Store diagnostic info about the coordinate transformation
    diagnosticDataRef.current.lastCoordTransform = {
      input: { clientX: e.clientX, clientY: e.clientY },
      rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height },
      canvasCoords: { canvasX, canvasY },
      scales: { charScale, canvasScale },
      offsets: { offsetX, offsetY },
      charCenter: { x: charCenterX, y: charCenterY },
      output: result
    }

    return result
  }

  const handleMouseDown = (e) => {
    if (currentStroke >= strokes.length) {
      logMeaningfulEvent('mousedown_ignored', { reason: 'all_strokes_complete' })
      return
    }

    const point = getCanvasCoordinates(e)
    const currentStrokePath = strokes[currentStroke]

    // Special handling for single-point strokes (dots)
    if (currentStrokePath.length === 1) {
      const targetPoint = currentStrokePath[0]
      const distance = Math.sqrt(
        Math.pow(point.x - targetPoint.x, 2) +
        Math.pow(point.y - targetPoint.y, 2)
      )

      logMeaningfulEvent('dot_tap', {
        clientX: e.clientX,
        clientY: e.clientY,
        canvasPoint: point,
        targetPoint,
        distance,
        threshold: 35,
        success: distance <= 35,
      })

      // If clicked within 35 pixels of the dot, mark it as cleared (larger threshold for touch screens)
      if (distance <= 35) {
        setClearedCheckpoints(new Set([0]))
        logMeaningfulEvent('dot_cleared', { strokeIndex: currentStroke })

        // Move to next stroke after a short delay
        setTimeout(() => {
          setClearedCheckpoints(new Set())
          const nextStroke = currentStroke + 1
          setCurrentStroke(nextStroke)
          logMeaningfulEvent('stroke_advanced', { fromStroke: currentStroke, toStroke: nextStroke })

          // Check if all strokes completed
          if (nextStroke >= strokes.length) {
            logMeaningfulEvent('all_strokes_complete', { totalStrokes: strokes.length })
            setTimeout(() => {
              onSuccess(true)
            }, 300)
          }
        }, 300)
      }
      return
    }

    setIsDrawing(true)
    setUserPath([point])
    setIsStrokeValid(true)

    // Check deviation for starting point
    const deviation = calculateDeviationFromPath(point, currentStrokePath)
    const startDeviationExceeded = deviation.distance > deviationThreshold
    if (startDeviationExceeded) {
      setIsStrokeValid(false)
    }

    // Log stroke start
    logMeaningfulEvent('stroke_start', {
      clientX: e.clientX,
      clientY: e.clientY,
      canvasPoint: point,
      deviation: deviation.distance,
      deviationThreshold,
      startDeviationExceeded,
      strokePathLength: currentStrokePath.length,
    })

    // Immediately check if starting point is near first checkpoint
    const result = evaluateTracePath([point], currentStrokePath)
    setClearedCheckpoints(result.clearedCheckpoints)
    if (result.clearedCheckpoints.size > 0) {
      logMeaningfulEvent('checkpoint_cleared', {
        checkpoints: Array.from(result.clearedCheckpoints),
        totalCheckpoints: currentStrokePath.length,
      })
    }
  }

  const handleMouseMove = (e) => {
    if (!isDrawing || currentStroke >= strokes.length) return
    const point = getCanvasCoordinates(e)

    // Check deviation for this point
    const currentStrokePath = strokes[currentStroke]
    const deviation = calculateDeviationFromPath(point, currentStrokePath)
    const deviationExceeded = deviation.distance > deviationThreshold
    if (deviationExceeded && isStrokeValid) {
      setIsStrokeValid(false)
      logMeaningfulEvent('deviation_exceeded', {
        canvasPoint: point,
        deviation: deviation.distance,
        deviationThreshold,
      })
    }

    setUserPath(prev => {
      const newPath = [...prev, point]

      // Update cleared checkpoints in real-time
      const result = evaluateTracePath(newPath, currentStrokePath)
      const prevSize = clearedCheckpoints.size
      if (result.clearedCheckpoints.size > prevSize) {
        // New checkpoint cleared - log it
        const newlyCleared = Array.from(result.clearedCheckpoints).filter(c => !clearedCheckpoints.has(c))
        logMeaningfulEvent('checkpoint_cleared', {
          newlyCleared,
          totalCleared: result.clearedCheckpoints.size,
          totalCheckpoints: currentStrokePath.length,
          canvasPoint: point,
        })
      }
      setClearedCheckpoints(result.clearedCheckpoints)

      return newPath
    })
  }

  const handleMouseUp = () => {
    if (!isDrawing) {
      logMeaningfulEvent('mouseup_ignored', { reason: 'not_drawing' })
      return
    }
    setIsDrawing(false)

    // Evaluate the traced path
    if (currentStroke < strokes.length) {
      // If stroke was invalid (deviated too much), discard it
      if (!isStrokeValid) {
        logMeaningfulEvent('stroke_failed', {
          reason: 'deviation_exceeded',
          userPathLength: userPath.length,
          clearedCheckpoints: Array.from(clearedCheckpoints),
          totalCheckpoints: strokes[currentStroke].length,
        })
        setTimeout(() => {
          setUserPath([])
          setClearedCheckpoints(new Set())
          setIsStrokeValid(true)
        }, 500)
        return
      }

      const result = evaluateTracePath(userPath, strokes[currentStroke])

      if (result.isCorrect) {
        logMeaningfulEvent('stroke_completed', {
          strokeIndex: currentStroke,
          userPathLength: userPath.length,
          clearedCheckpoints: Array.from(clearedCheckpoints),
          totalCheckpoints: strokes[currentStroke].length,
        })

        // Move to next stroke (this makes the completed stroke turn green)
        // Note: clearedCheckpoints will be automatically cleared by the useEffect when currentStroke changes
        const nextStroke = currentStroke + 1
        setCurrentStroke(nextStroke)

        logMeaningfulEvent('stroke_advanced', { fromStroke: currentStroke, toStroke: nextStroke })

        // Check if all strokes completed after a short delay to show the green stroke
        if (nextStroke >= strokes.length) {
          logMeaningfulEvent('all_strokes_complete', { totalStrokes: strokes.length })
          setTimeout(() => {
            onSuccess(true)
          }, 300)
        }
      } else {
        // Not correct, let them try again
        logMeaningfulEvent('stroke_failed', {
          reason: 'incomplete_checkpoints',
          strokeIndex: currentStroke,
          userPathLength: userPath.length,
          clearedCheckpoints: Array.from(clearedCheckpoints),
          totalCheckpoints: strokes[currentStroke].length,
          evaluationResult: result,
        })
        setTimeout(() => {
          setUserPath([])
          setClearedCheckpoints(new Set())
          setIsStrokeValid(true)
        }, 500)
      }
    }
  }

  const handleReset = () => {
    setUserPath([])
    setCurrentStroke(0)
    setClearedCheckpoints(new Set())
    setIsStrokeValid(true)
    onSuccess(false)
  }

  if (!strokes || strokes.length === 0) {
    return (
      <div className="handwriting-canvas">
        <div className="no-character">
          Character not available
        </div>
      </div>
    )
  }

  return (
    <div className="handwriting-canvas">
      <div className="canvas-wrapper">
        <canvas
          ref={canvasRef}
          width={256}
          height={256 + VERTICAL_PADDING * 2}
          className="drawing-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            if (isDrawing) {
              handleMouseUp()
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault()
            if (e.touches.length > 0) {
              const touch = e.touches[0]
              // Track touch event for diagnostics
              diagnosticDataRef.current.touchStartCount++
              diagnosticDataRef.current.lastTouchStartTime = Date.now()
              const touchData = {
                type: 'touchstart',
                timestamp: Date.now(),
                clientX: touch.clientX,
                clientY: touch.clientY,
                pageX: touch.pageX,
                pageY: touch.pageY,
                radiusX: touch.radiusX,
                radiusY: touch.radiusY,
                force: touch.force,
                touchCount: e.touches.length,
                currentStroke,
              }
              diagnosticDataRef.current.touchEvents.push(touchData)
              // Keep only last 50 events
              if (diagnosticDataRef.current.touchEvents.length > 50) {
                diagnosticDataRef.current.touchEvents.shift()
              }
              // Log meaningful event
              logMeaningfulEvent('touchstart', {
                clientX: touch.clientX,
                clientY: touch.clientY,
                pageX: touch.pageX,
                pageY: touch.pageY,
                radiusX: touch.radiusX,
                radiusY: touch.radiusY,
                force: touch.force,
                touchCount: e.touches.length,
                touchStartCount: diagnosticDataRef.current.touchStartCount,
              })
              handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY })
            }
          }}
          onTouchMove={(e) => {
            e.preventDefault()
            if (e.touches.length > 0) {
              const touch = e.touches[0]
              // Track touch event for diagnostics
              diagnosticDataRef.current.touchEvents.push({
                type: 'touchmove',
                timestamp: Date.now(),
                clientX: touch.clientX,
                clientY: touch.clientY,
                pageX: touch.pageX,
                pageY: touch.pageY,
                force: touch.force,
                touchCount: e.touches.length,
              })
              // Keep only last 50 events
              if (diagnosticDataRef.current.touchEvents.length > 50) {
                diagnosticDataRef.current.touchEvents.shift()
              }
              // Log meaningful event (sample every 5th move to avoid too much noise)
              if (diagnosticDataRef.current.meaningfulEvents.filter(e => e.type === 'touchmove').length % 5 === 0) {
                logMeaningfulEvent('touchmove', {
                  clientX: touch.clientX,
                  clientY: touch.clientY,
                  pageX: touch.pageX,
                  pageY: touch.pageY,
                  force: touch.force,
                  touchCount: e.touches.length,
                })
              }
              handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY })
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault()
            // Track touch event for diagnostics
            diagnosticDataRef.current.touchEvents.push({
              type: 'touchend',
              timestamp: Date.now(),
            })
            // Log meaningful event
            logMeaningfulEvent('touchend', {
              changedTouchesCount: e.changedTouches?.length || 0,
            })
            handleMouseUp()
          }}
          onTouchCancel={(e) => {
            // Track touch event for diagnostics
            diagnosticDataRef.current.touchEvents.push({
              type: 'touchcancel',
              timestamp: Date.now(),
            })
            // Log meaningful event - important for debugging Safari issues
            logMeaningfulEvent('touchcancel', {
              reason: 'browser_cancelled',
              changedTouchesCount: e.changedTouches?.length || 0,
            })
            // Treat cancel same as end (Safari may cancel touch events)
            handleMouseUp()
          }}
        />
      </div>
    </div>
  )
})

export default HandwritingCanvas
